import { Fragment, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Modal } from '../primitives';

/**
 * Tlačítko „Nový formulář" do lišty dokumentu.
 *
 * Když se dělá víc protokolů za sebou, uživatel po uložení potřeboval formulář
 * vyčistit — dosud šlo jen reloadnout stránku (a autosave koncept pak nabídl
 * obnovení právě uloženého). Tohle tlačítko po potvrzení:
 *   1. smaže rozpracovaný koncept v prohlížeči (`useDocDraft` klíč),
 *   2. přejde na čistou URL formuláře (bez `?id=`), takže se opustí i editace
 *      záznamu z archivu,
 *   3. přes `FreshDocRoute` znovu namountuje stránku → všechen `useState`
 *      se vrátí na výchozí hodnoty.
 *
 * Remount je řízen `location.state.fresh` — nezanechává nic v URL a
 * po reloadu stránky se nespouští znovu.
 */
export interface NewDocButtonProps {
  /** Klíč konceptu — stejný, jaký stránka dává do `useDocDraft`. */
  draftKey: string;
  /** Cesta formuláře, např. `/app/predavaci-dilo`. */
  path: string;
  label?: ReactNode;
}

const DRAFT_PREFIX = 'sitedoc:draft:';

export function NewDocButton({ draftKey, path, label = 'Nový formulář' }: NewDocButtonProps) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  function confirm() {
    const key = DRAFT_PREFIX + draftKey;
    const wipe = () => {
      try {
        localStorage.removeItem(key);
      } catch {
        /* nedostupné úložiště — není co mazat */
      }
    };
    wipe();
    // Pojistka: debounce v useDocDraft mohl mít naplánovaný zápis těsně před
    // odmountováním staré stránky — po jeho okně koncept smažeme ještě jednou.
    window.setTimeout(wipe, 1000);
    setOpen(false);
    navigate(path, { replace: true, state: { fresh: Date.now() } });
  }

  return (
    <>
      <Button
        variant="secondary"
        leading="＋"
        title="Vyčistit formulář a začít nový dokument"
        onClick={() => setOpen(true)}
      >
        {label}
      </Button>
      <Modal open={open} onClose={() => setOpen(false)} title="Začít nový formulář?">
        <p style={{ margin: 0, fontSize: 14, lineHeight: 1.5 }}>
          Formulář se vyčistí na výchozí hodnoty. Pokud jste rozpracovaný dokument
          neuložili tlačítkem <strong>Uložit</strong>, jeho údaje se ztratí — včetně
          automaticky uloženého konceptu.
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            Zpět
          </Button>
          <Button variant="primary" onClick={confirm}>
            Vyčistit a začít nový
          </Button>
        </div>
      </Modal>
    </>
  );
}

/**
 * Obal routy formuláře: změní `key` podřízeného stromu podle
 * `location.state.fresh`, což React donutí stránku kompletně přemountovat
 * (viz `NewDocButton`). Běžná navigace (`?id=…`) remount nespouští.
 */
export function FreshDocRoute({ children }: { children: ReactNode }) {
  const location = useLocation();
  const fresh = (location.state as { fresh?: number } | null)?.fresh ?? 0;
  return <Fragment key={fresh}>{children}</Fragment>;
}
